import * as React from 'react';
import { X } from 'lucide-react';

import { cn } from '../lib/utils';
import { Badge } from '../primitives/badge';
import { Button } from '../primitives/button';

/**
 * One bulk action available against the current selection. Operators
 * commonly need: assign, approve, export, close, release, hold, shelve,
 * acknowledge, retire, rollback.
 *
 * Each action receives the array of selected ids when fired — the
 * consuming view does the API fan-out (parallel mutations, batch
 * endpoint, whatever is appropriate). Wire bulk failures back to the
 * toast / ViewState layer.
 */
export interface BulkAction<Id = string> {
  id: string;
  label: string;
  icon?: React.ReactNode;
  variant?: 'default' | 'outline' | 'ghost' | 'destructive' | 'secondary';
  /** Async — toolbar shows a spinner state. */
  onClick: (selectedIds: Id[]) => Promise<void> | void;
  /** Disable for the current selection (e.g. only applies to certain statuses). */
  disabled?: (selectedIds: Id[]) => boolean;
  /** Tooltip / a11y hint. */
  hint?: string;
}

export interface BulkActionsToolbarProps<Id = string> {
  selectedIds: Id[];
  actions: BulkAction<Id>[];
  /** Called when the user clears the selection. */
  onClear: () => void;
  /** Optional summary suffix, e.g. " items totalling $12,345.00". */
  summary?: React.ReactNode;
  /** Hide the toolbar when nothing is selected (default true). */
  hideWhenEmpty?: boolean;
  className?: string;
}

/**
 * Sticky bulk-actions strip that appears above a list when one or
 * more rows are selected. Operators get the canonical action set
 * (assign / approve / export / close / release / hold / shelve /
 * acknowledge / retire / rollback) without having to fan out per row.
 *
 * Apply to:
 *   - PLM parts/documents lists
 *   - Quality NCR queue
 *   - Inventory transactions
 *   - Production runs
 *   - Active alarms
 *   - Finance journals
 *
 * @example
 * <BulkActionsToolbar
 *   selectedIds={selected}
 *   onClear={() => setSelected([])}
 *   actions={[
 *     { id:'approve', label:'Approve', onClick: ids => approveMany(ids) },
 *     { id:'release', label:'Release', variant:'default', onClick: ids => releaseMany(ids) },
 *     { id:'hold', label:'Hold', variant:'destructive', onClick: ids => holdMany(ids) },
 *   ]}
 *   summary={`${selected.length} of ${rows.length} selected`}
 * />
 */
export function BulkActionsToolbar<Id = string>({
  selectedIds,
  actions,
  onClear,
  summary,
  hideWhenEmpty = true,
  className,
}: BulkActionsToolbarProps<Id>) {
  const [pendingId, setPendingId] = React.useState<string | null>(null);

  if (hideWhenEmpty && selectedIds.length === 0) return null;

  return (
    <div
      className={cn(
        'sticky top-0 z-10 flex flex-wrap items-center gap-2',
        'rounded-md border border-primary/40 bg-primary/5 px-3 py-2 shadow-sm',
        className,
      )}
      role="toolbar"
      aria-label="Bulk actions"
      data-testid="bulk-actions-toolbar"
    >
      <Badge variant="secondary" className="font-mono">
        {selectedIds.length} selected
      </Badge>

      {summary && (
        <span className="text-sm text-muted-foreground">{summary}</span>
      )}

      <div className="flex-1" />

      {actions.map((a) => {
        const isDisabled = a.disabled?.(selectedIds) ?? false;
        const isPending = pendingId === a.id;
        return (
          <Button
            key={a.id}
            variant={a.variant ?? 'outline'}
            size="sm"
            disabled={isDisabled || isPending || selectedIds.length === 0}
            title={a.hint}
            aria-label={a.hint ?? a.label}
            onClick={async () => {
              setPendingId(a.id);
              try {
                await a.onClick(selectedIds);
              } finally {
                setPendingId(null);
              }
            }}
          >
            {a.icon && <span className="mr-1 inline-flex">{a.icon}</span>}
            {isPending ? 'Working…' : a.label}
          </Button>
        );
      })}

      <Button
        variant="ghost"
        size="sm"
        onClick={onClear}
        aria-label="Clear selection"
        title="Clear selection"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </Button>
    </div>
  );
}

BulkActionsToolbar.displayName = 'BulkActionsToolbar';
